import { useCallback, useMemo } from 'react';

import { useTheme } from '@/shared/ui';

import useRegularOptions from '@/features/_shared/chart/hooks/options/useRegularOptions';
import getRegularTooltip from '@/features/_shared/chart/hooks/tooltip/getRegularTooltip';
import useForwardCurveTooltipFormatter from '@/features/_shared/chart/hooks/tooltip/useForwardCurveTooltipFormatter';
import { withForwardCurveSeriesAppearance } from '@/features/_shared/chart/lib/applyChartSeriesAppearance';
import { createChartAppearance } from '@/features/_shared/chart/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/_shared/chart/types/highcharts';

import type { XAxisOptions } from 'highcharts';

/**
 * Configures X-axis options for forward curve charts.
 */
const configureXAxis = (xAxisOptions: XAxisOptions): void => {
  xAxisOptions.type = 'linear';
  xAxisOptions.tickInterval = 1;
  xAxisOptions.crosshair = true;

  // Remove formatter if present to use default behavior
  if (xAxisOptions.labels?.formatter) {
    xAxisOptions.labels.formatter = undefined;
  }
};

/**
 * Hook for creating Highcharts options for forward curve charts.
 * @param height - Optional chart height (multiplied by row height in base options)
 * @param preserveColors - When true, skips color assignment (used by seasonal charts)
 * @returns Object with createOptions function
 */
const useForwardCurveOptions = (height?: number, preserveColors?: boolean) => {
  const { createOptions: createRegularOptions } = useRegularOptions(height);
  const { createTooltipFormatter } = useForwardCurveTooltipFormatter();
  const theme = useTheme();

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, theme).tooltip,
    [createTooltipFormatter, theme]
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const appearance = createChartAppearance(theme, 'regular');
      const coloredSeries = preserveColors
        ? chartSeries
        : chartSeries.map(series => withForwardCurveSeriesAppearance(series, appearance));

      const baseOptions = createRegularOptions(coloredSeries, hiddenSeriesIds);

      const options: ExtendedOptions = {
        ...baseOptions,
        navigator: { enabled: false },
        rangeSelector: { enabled: false }
      };

      // Configure X-axis if present (mutates only the cloned options object, not input)
      const xAxisOptions = options.xAxis as XAxisOptions | undefined;
      if (xAxisOptions) {
        configureXAxis(xAxisOptions);
      }

      return {
        ...options,
        xAxis: xAxisOptions,
        tooltip,
        chart: {
          ...options.chart,
          marginTop: 40,
          zooming: { type: undefined }
        },
        plotOptions: {
          ...options.plotOptions,
          series: {
            ...options.plotOptions?.series,
            dataLabels: {
              enabled: false
            }
          }
        }
      };
    },
    [createRegularOptions, preserveColors, theme, tooltip]
  );

  return { createOptions };
};

export default useForwardCurveOptions;
